import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function CountersPage() {
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState("");
  const [counters, setCounters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ counter_name: "", counter_number: 1, allowed_categories: [], is_active: true });

  useEffect(() => {
    api.get("/agencies").then((r) => {
      setAgencies(r.data);
      if (r.data[0]) setAgencyId(r.data[0].id);
    });
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const refresh = useCallback(() => {
    if (!agencyId) return;
    api.get("/counters", { params: { agency_id: agencyId } }).then((r) => setCounters(r.data));
  }, [agencyId]);
  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    try {
      const payload = { ...form, agency_id: agencyId };
      if (editing) {
        await api.patch(`/counters/${editing.id}`, payload);
        toast.success("Guichet mis à jour");
      } else {
        await api.post("/counters", payload);
        toast.success("Guichet créé");
      }
      setOpen(false); setEditing(null);
      setForm({ counter_name: "", counter_number: 1, allowed_categories: [], is_active: true });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const onEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };
  const onDelete = async (c) => {
    if (!confirm(`Supprimer ${c.counter_name} ?`)) return;
    await api.delete(`/counters/${c.id}`); toast.success("Guichet supprimé"); refresh();
  };

  const toggleCategory = (code) => {
    setForm((f) => ({
      ...f,
      allowed_categories: f.allowed_categories.includes(code)
        ? f.allowed_categories.filter((x) => x !== code)
        : [...f.allowed_categories, code],
    }));
  };

  return (
    <div className="space-y-6" data-testid="counters-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl">Guichets</h1>
          <p className="text-slate-500 text-sm mt-1">Configurez les guichets par agence et leurs catégories.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={agencyId} onValueChange={setAgencyId}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Agence" /></SelectTrigger>
            <SelectContent>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ counter_name: "", counter_number: 1, allowed_categories: [], is_active: true }); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-counter-button">
                <Plus className="w-4 h-4 mr-1" /> Nouveau guichet
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau"} guichet</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nom</Label><Input value={form.counter_name} onChange={(e) => setForm({ ...form, counter_name: e.target.value })} placeholder="Guichet 1" /></div>
                  <div><Label>Numéro</Label><Input type="number" value={form.counter_number} onChange={(e) => setForm({ ...form, counter_number: parseInt(e.target.value || "0") })} /></div>
                </div>
                <div>
                  <Label>Catégories servies</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-md">
                    {categories.map((c) => (
                      <label key={c.code} className="flex items-center gap-2 text-sm">
                        <Checkbox checked={form.allowed_categories.includes(c.code)} onCheckedChange={() => toggleCategory(c.code)} />
                        <span style={{ color: c.color }} className="font-mono font-bold">{c.code}</span>
                        <span className="text-slate-600">{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Actif</Label></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={submit} className="bg-[#DC2626] hover:bg-[#B91C1C]">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="text-left p-3">Numéro</th><th className="text-left">Nom</th><th className="text-left">Catégories</th><th>État</th><th></th></tr>
          </thead>
          <tbody>
            {counters.map((c) => (
              <tr key={c.id} className="border-t border-slate-200">
                <td className="p-3 font-mono font-bold">#{c.counter_number}</td>
                <td className="font-medium">{c.counter_name}</td>
                <td><div className="flex flex-wrap gap-1">{c.allowed_categories?.map((code) => <span key={code} className="text-xs px-2 py-0.5 bg-slate-100 rounded">{code}</span>)}</div></td>
                <td className="text-center">{c.is_active ? <span className="text-[#16A34A] text-xs font-bold">ACTIF</span> : <span className="text-slate-400 text-xs">INACTIF</span>}</td>
                <td className="text-right p-3">
                  <Button size="icon" variant="ghost" onClick={() => onEdit(c)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => onDelete(c)}><Trash2 className="w-4 h-4 text-[#DC2626]" /></Button>
                </td>
              </tr>
            ))}
            {counters.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-400">Aucun guichet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
